import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../lib/supabase';
import { useAuth } from '../contexts/AuthContext';
import RoleBasedHeader from '../components/ui/RoleBasedHeader';
import Icon from '../components/AppIcon';

const getPasswordStrength = (password) => {
  if (!password) return { score: 0, label: '', color: '' };
  let score = 0;
  if (password?.length >= 8) score++;
  if (password?.length >= 12) score++;
  if (/[A-Z]/?.test(password)) score++;
  if (/[0-9]/?.test(password)) score++;
  if (/[^A-Za-z0-9]/?.test(password)) score++;
  if (score <= 1) return { score, label: 'Weak', color: 'bg-red-500' };
  if (score <= 3) return { score, label: 'Fair', color: 'bg-yellow-500' };
  if (score === 4) return { score, label: 'Good', color: 'bg-blue-500' };
  return { score, label: 'Strong', color: 'bg-green-500' };
};

const ChangePassword = () => {
  const navigate = useNavigate();
  const { user, loading } = useAuth();

  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  const strength = getPasswordStrength(newPassword);

  useEffect(() => {
    if (!loading && !user) navigate('/login-authentication');
  }, [user, loading, navigate]);

  const validate = () => {
    const newErrors = {};
    if (!currentPassword) newErrors.currentPassword = 'Current password is required';
    if (!newPassword) newErrors.newPassword = 'New password is required';
    else if (newPassword?.length < 8) newErrors.newPassword = 'Password must be at least 8 characters';
    if (!confirmPassword) newErrors.confirmPassword = 'Please confirm your new password';
    else if (newPassword !== confirmPassword) newErrors.confirmPassword = 'Passwords do not match';
    return newErrors;
  };

  const handleSubmit = async (e) => {
    e?.preventDefault();
    setSuccessMessage('');
    setErrorMessage('');
    const validationErrors = validate();
    if (Object.keys(validationErrors)?.length > 0) {
      setErrors(validationErrors);
      return;
    }
    setIsSubmitting(true);
    try {
      // Re-authenticate with current password first
      const { error: signInError } = await supabase?.auth?.signInWithPassword({
        email: user?.email,
        password: currentPassword,
      });
      if (signInError) {
        setErrors({ currentPassword: 'Current password is incorrect' });
        setIsSubmitting(false);
        return;
      }
      // Update password
      const { error } = await supabase?.auth?.updateUser({ password: newPassword });
      if (error) throw error;
      setSuccessMessage('Password changed successfully!');
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      setErrors({});
    } catch (err) {
      setErrorMessage(err?.message || 'Failed to change password. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const inputClass = (field) =>
    `w-full px-3 py-2.5 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1e4a72] focus:border-transparent transition-colors pr-10 ${errors?.[field] ? 'border-red-400 bg-red-50' : 'border-gray-300 bg-white'}`;

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <RoleBasedHeader />
        <div className="flex items-center justify-center min-h-[60vh]">
          <div className="w-10 h-10 border-4 border-[#1e4a72] border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <RoleBasedHeader />
      <div className="max-w-md mx-auto px-4 py-10">
        {/* Page Header */}
        <div className="mb-6 flex items-center gap-3">
          <button
            onClick={() => navigate(-1)}
            className="p-2 rounded-lg hover:bg-gray-200 transition-colors"
          >
            <Icon name="ArrowLeft" size={20} className="text-gray-600" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-[#1e4a72]">Change Password</h1>
            <p className="text-sm text-gray-500 mt-0.5">Keep your account secure</p>
          </div>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-6">
          {/* Success Message */}
          {successMessage && (
            <div className="mb-5 flex items-center gap-3 p-4 bg-green-50 border border-green-200 rounded-xl text-green-700 text-sm">
              <Icon name="CheckCircle" size={18} className="flex-shrink-0" />
              <span>{successMessage}</span>
            </div>
          )}

          {/* Error Message */}
          {errorMessage && (
            <div className="mb-5 flex items-center gap-3 p-4 bg-red-50 border border-red-200 rounded-xl text-red-700 text-sm">
              <Icon name="AlertCircle" size={18} className="flex-shrink-0" />
              <span>{errorMessage}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Current Password */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Current Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showCurrent ? 'text' : 'password'}
                  value={currentPassword}
                  onChange={(e) => { setCurrentPassword(e?.target?.value); if (errors?.currentPassword) setErrors(p => ({ ...p, currentPassword: '' })); }}
                  placeholder="Enter current password"
                  className={inputClass('currentPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrent(!showCurrent)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon name={showCurrent ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
              {errors?.currentPassword && <p className="text-xs text-red-500 mt-1">{errors?.currentPassword}</p>}
            </div>

            {/* New Password */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showNew ? 'text' : 'password'}
                  value={newPassword}
                  onChange={(e) => { setNewPassword(e?.target?.value); if (errors?.newPassword) setErrors(p => ({ ...p, newPassword: '' })); }}
                  placeholder="Min. 8 characters"
                  className={inputClass('newPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowNew(!showNew)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon name={showNew ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
              {errors?.newPassword && <p className="text-xs text-red-500 mt-1">{errors?.newPassword}</p>}

              {/* Password Strength */}
              {newPassword && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5]?.map((i) => (
                      <div
                        key={i}
                        className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${i <= strength?.score ? strength?.color : 'bg-gray-200'}`}
                      />
                    ))}
                  </div>
                  <p className={`text-xs font-medium ${strength?.score <= 1 ? 'text-red-500' : strength?.score <= 3 ? 'text-yellow-600' : strength?.score === 4 ? 'text-blue-600' : 'text-green-600'}`}>
                    {strength?.label}
                  </p>
                </div>
              )}
            </div>

            {/* Confirm Password */}
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Confirm New Password <span className="text-red-500">*</span>
              </label>
              <div className="relative">
                <input
                  type={showConfirm ? 'text' : 'password'}
                  value={confirmPassword}
                  onChange={(e) => { setConfirmPassword(e?.target?.value); if (errors?.confirmPassword) setErrors(p => ({ ...p, confirmPassword: '' })); }}
                  placeholder="Re-enter new password"
                  className={inputClass('confirmPassword')}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
                >
                  <Icon name={showConfirm ? 'EyeOff' : 'Eye'} size={16} />
                </button>
              </div>
              {errors?.confirmPassword && <p className="text-xs text-red-500 mt-1">{errors?.confirmPassword}</p>}
              {!errors?.confirmPassword && confirmPassword && newPassword === confirmPassword && (
                <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                  <Icon name="CheckCircle" size={12} /> Passwords match
                </p>
              )}
            </div>

            <button
              type="submit"
              disabled={isSubmitting}
              className="w-full flex items-center justify-center gap-2 px-6 py-3 text-sm font-semibold text-white bg-gradient-to-r from-[#1e4a72] to-[#ff8c00] hover:opacity-90 rounded-xl transition-all duration-200 shadow-md disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {isSubmitting ? (
                <>
                  <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
                  <span>Updating...</span>
                </>
              ) : (
                <>
                  <Icon name="KeyRound" size={16} />
                  <span>Update Password</span>
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ChangePassword;
